import React, { useCallback, useEffect, useRef, useState, FC } from 'react'

import { FieldError, Merge } from 'react-hook-form'
import styled, { css } from 'styled-components'

import { Switch } from '@style-port/roov-ui-components-react'
import { Icon, Txt } from 'src/components/atoms'

const Container = styled.div`
  width: 100%;
  min-height: 3.57rem;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 1rem;
  max-height: 26.8rem;
  overflow: auto;
  transition: all 0.1s ease-in-out;

  &:hover:not(:focus):not(:disabled) {
    box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.1);
  }
`
const ItemListWrapper = styled.div<{ existEnteredItem: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  ${({ existEnteredItem }) =>
    existEnteredItem &&
    css`
      gap: 0.57rem;
    `}
`
const StyledSwitch = styled(Switch)`
  width: fit-content;
`
const ContentTextAreaContainer = styled.div`
  height: 2rem;
`
const ContentTextArea = styled.span<{ existEnteredItem: boolean }>`
  outline: none;
  display: inline-block;
  flex-shrink: 1;

  // まだ入力がされていない場合、widthを100%にする事でtextarea要素に当たる部分のどこをクリックしてもfocusされる
  ${({ existEnteredItem }) =>
    !existEnteredItem &&
    css`
      width: 100%;
    `}
`

type Props = {
  emails: string[] | undefined
  updateEmails: (emails: string[]) => void
  emailFieldErrors: Merge<FieldError, Array<FieldError | undefined>> | undefined
  onTrigger: () => void
  className?: string
}

export const UserInvitationTextArea: FC<Props> = ({
  emails,
  updateEmails,
  emailFieldErrors,
  onTrigger,
  className,
}) => {
  const textAreaReference = useRef<HTMLDivElement>(null)
  // テキストが何かしら入力されているかを判断する
  const [dummyText, setDummyText] = useState('')

  const handleInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line unicorn/prefer-dom-node-text-content
    const inputText = event.target.innerText
    // dummyのstateに入れることで textAreaRef.current?.innerText で入力された文字を取得出来る
    setDummyText(inputText)

    // スペース・,・改行（改行を行うと<br>タグが入る） を元にテキストを分割する
    const splitTexts = inputText.split(/[\s,]/)
    // 分割した文字列から必要な文字のみを残す
    const cleanedTexts = splitTexts.filter((text, index) => {
      // 配列最後の要素は空白でも必要（コピペではなく普通に入力を行った際の後続の判定で用いるため）になるが、
      // それ以外の空白はコピペを行った時のゴミデータとなるため排除する
      return splitTexts.length - 1 === index || text
    })

    // 分割が行え、かつ入力された文字が分割を行うための文字だけでなかった場合
    if (cleanedTexts.length > 1 && cleanedTexts[0] !== '') {
      // 最後の配列以外を入力されたemailとする。改行入力時に空白が入ってしまう事があるため、空文字などをfilterする
      let inputEmails = cleanedTexts.splice(0, cleanedTexts.length - 1).filter(Boolean)
      // email部分を排除した最後のテキスト部分
      const lastText = cleanedTexts.splice(-1)[0]

      // 最後に入力したテキストが空白でなかった場合（コピペなどして複数のメールアドレスを入力した場合など）はemailとする
      if (lastText !== '') {
        inputEmails = [...inputEmails, lastText]
      }

      // 今まで入力されたemailと入力されたemailと合算する
      const updatedEmails = emails ? [...emails, ...inputEmails] : inputEmails
      updateEmails(updatedEmails)

      // emailFieldErrorsの値を更新するためにtriggerを実行する
      onTrigger()

      // 無理矢理だが textArea に見立てたdiv要素に空文字を設定する
      if (textAreaReference?.current) {
        textAreaReference.current.innerHTML = ''
      }
      textAreaReference?.current && setDummyText('')
    }
  }

  const handleOnBlur = () => {
    if (dummyText.length === 0) {
      return
    }
    // textAreaに入力されていてフォーカスが外れた場合は強制的に区切り文字を入れて認識させる
    setDummyText(`${dummyText},`)
    if (textAreaReference.current) {
      textAreaReference.current.innerHTML = `${dummyText},`
    }

    // eventを強制的に発生させて handleInput を呼び出す
    const input = document.querySelector('.content-text-area') as HTMLInputElement
    // @ts-expect-error NOTE: clear the interal value to force an actual change
    // eslint-disable-next-line no-underscore-dangle
    input._valueTracker?.setValue('')
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  // プレースホルダーに見立てたテキストをクリックした際はtextArea部分にカーソルが当たるようにする
  const handleOnClickPlaceholderText = () => {
    const textArea = textAreaReference.current
    if (textArea) {
      textArea.focus()
    }
  }

  const handleOnClickEnteredEmail = async (emailIndex: number) => {
    const updatedEmails = emails?.filter((email, index) => index !== emailIndex) ?? []
    updateEmails(updatedEmails)

    // emailFieldErrorsの値を更新するためにtriggerを実行する
    onTrigger()
  }

  const handleKeyDown = useCallback(
    async (event_: KeyboardEvent) => {
      if (event_.key === 'Backspace') {
        // 戻るボタンを押された際に 既に入力済みのemailがあり、まだtextAreaには何も入力していなかった場合、最後のemailを削除する
        // eslint-disable-next-line unicorn/prefer-dom-node-text-content
        const inputText = textAreaReference?.current ? textAreaReference.current.innerText : ''

        const enteredEmails = emails
        if (enteredEmails && enteredEmails.length > 0 && (!inputText || inputText === '')) {
          const updatedEmails = enteredEmails?.filter(
            (email, index) => index !== enteredEmails.length - 1
          )
          updateEmails(updatedEmails)

          // emailFieldErrorsの値を更新するためにtriggerを実行する
          onTrigger()
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emails]
  )

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    // テキストを含まない（画像など）場合は何もしない
    const { types } = event.clipboardData
    if (!types || !types.includes('text/plain')) {
      event.preventDefault()
      return
    }

    // クリップボードの中身を取得
    const text = event.clipboardData.getData('text/plain')

    // キャレットの位置取得して貼り付け
    // ref: https://developer.mozilla.org/ja/docs/Web/API/Element/paste_event
    const selection = window.getSelection()
    if (!selection || !selection.rangeCount) {
      throw new Error('不明なエラー')
    }
    selection.deleteFromDocument()
    selection.getRangeAt(0).insertNode(document.createTextNode(text))

    // ペーストした後に自動的に文字列分割を行う（handleKeyDownを反応させる）ためにpreventDefaultは行わない
    // event.preventDefault()
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false)
    // unmount時に eventListener を削除する事で何度も同じイベントが登録されてしまうのを防ぐ
    return () => document.removeEventListener('keydown', handleKeyDown, false)
  }, [handleKeyDown])

  return (
    <div className={className}>
      <Container className="tw-border tw-border-border-secondary focus:tw-border-roovPrimary-default">
        <ItemListWrapper existEnteredItem={!!emails && emails.length > 0} className="tw-relative">
          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
          <>
            {emails?.map((email, index) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <span key={`${email}-${index}`}>
                  <StyledSwitch
                    switchType={emailFieldErrors && emailFieldErrors[index] ? 'error' : 'primary'}
                    onClick={() => handleOnClickEnteredEmail(index)}
                  >
                    <div className="tw-flex tw-items-center">
                      <div>{email}</div>
                      <Icon path="close/soft/line" size="--r1" className="tw-ml-s-3" />
                    </div>
                  </StyledSwitch>
                </span>
              )
            })}
          </>

          <ContentTextAreaContainer
            className={`tw-centering ${emails && emails?.length > 0 ? '' : 'tw-w-full'}`}
          >
            <ContentTextArea
              contentEditable
              onInput={handleInput}
              onBlur={handleOnBlur}
              onPaste={handlePaste}
              ref={textAreaReference}
              existEnteredItem={!!emails && emails.length > 0}
              className="content-text-area tw-caret-correctGreen-default"
            />
          </ContentTextAreaContainer>
          {/* emailが1つも入力されていなく、textAreaでの入力もない場合、Placeholderに見立てたテキストを表示する */}
          {(!emails || emails.length === 0) && !textAreaReference.current?.innerText && (
            <div role="presentation" onClick={handleOnClickPlaceholderText} className="tw-h-full">
              <Txt
                className="tw-text-standardText-disable tw-absolute tw-top-[0.5rem] tw-left-0" // 文字が表示される位置をtopを使って微調整
              >
                user@example.co.jp（メールリストをコピーしてペースト可）
              </Txt>
            </div>
          )}
        </ItemListWrapper>
      </Container>
    </div>
  )
}
